/**
 * Navigation service for fetching navigation data
 */

import { transformSiteId } from '../helpers/url.helper';

// Import DOM types for fetch API
type FetchResponse = {
  ok: boolean;
  status: number;
  json: () => Promise<any>;
};

// Define a fetch function that uses window.fetch
const fetchData = (url: string, options?: { method: string }): Promise<FetchResponse> => {
  return window.fetch(url, options);
};

/**
 * Get script attributes from the current script tag
 */
function getScriptAttributes(): {
  tenant: string;
  siteId: string;
  locale: string;
  id: string;
  externalUrl: string;
} {
  // In a real implementation, we would use getCurrentScript()
  // For now, we'll use a placeholder implementation
  const scriptTag = document.querySelector('script[data-visa-components]');

  return {
    tenant: scriptTag?.getAttribute('tenant') || '',
    siteId: scriptTag?.getAttribute('siteid') || '',
    locale: scriptTag?.getAttribute('locale') || '',
    id: scriptTag?.getAttribute('id') || '',
    externalUrl: scriptTag?.getAttribute('externalurl') || '',
  };
}

/**
 * Fetch navigation data from the API
 * @param customUrl Optional custom URL to fetch navigation data from
 */
export async function fetchNavigationData(customUrl?: string): Promise<any> {
  const { tenant, siteId, locale, id, externalUrl } = getScriptAttributes();
  const transformedSiteId = siteId ? transformSiteId(siteId) : '';

  try {
    // Use a default API URL for now
    // In a real implementation, we would use environment variables
    const apiBaseUrl = 'https://api.visa.com/navigation';

    // Use custom URL if provided, otherwise use script attributes or default URL
    const fetchUrl =
      customUrl ||
      externalUrl ||
      apiBaseUrl
        .replace('{{TENANT}}', tenant)
        .replace('{{SITE_ID}}', transformedSiteId)
        .replace('{{LOCALE}}', locale)
        .replace('{{ID}}', id)
        .replace('{{SITE_ID_PARAM}}', siteId);

    const response = await fetchData(fetchUrl, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch navigation data: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching navigation data:', error);
    throw error;
  }
}

/**
 * For development and testing, we can use a mock navigation data
 * @param customUrl Optional custom URL to fetch navigation data from
 */
export async function fetchMockNavigationData(customUrl?: string): Promise<any> {
  try {
    // If custom URL is provided, use it
    if (customUrl) {
      try {
        const response = await fetchData(customUrl);
        if (response.ok) {
          return await response.json();
        }
      } catch (error) {
        console.log('Error fetching from custom URL:', error);
        console.log('Falling back to default sources');
      }
    }

    let response;
    try {
      response = await fetchData('/assets/nav.json');
      if (!response.ok) {
        throw new Error('Not found in /assets');
      }
    } catch {
      // Fallback to src/assets if the first attempt fails
      response = await fetchData('/src/assets/nav.json');
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch mock navigation data: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching mock navigation data:', error);
    throw error;
  }
}
